// DeleteRoomDialog — confirmation shown before a surveyor hard-deletes a
// room from the capture screen (ADR-015).
//
// Two flavours, driven by `capturedCount` (computed client-side from the
// room's already-loaded capture items by `countCapturedAssets`):
//
//   - capturedCount === 0 → LIGHT "Delete this room?" confirm. The room
//     holds no captured counts, so removing it is low-stakes.
//   - capturedCount > 0   → LOUD confirm. Per ADR-015 the dialog must
//     state explicitly that N captured assets will be permanently
//     destroyed before the surveyor can proceed, because a confirmed
//     delete removes contractual baseline counts with no in-app recovery
//     (hard delete).
//
// Layered on the shared `<Dialog>` primitive (focus trap, Esc-to-close,
// overlay dismiss, return-focus-to-invoker). Esc / overlay / Cancel close
// WITHOUT deleting — only the destructive button fires `onConfirm`.

import { type ReactElement } from 'react'

import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'

export interface DeleteRoomDialogProps {
  open: boolean
  /** Display name of the room being deleted (e.g. "Meeting Room 1"). */
  roomName: string
  /**
   * Number of captured assets in the room (positive recorded quantities).
   * 0 → light confirm; > 0 → loud "permanently delete N" confirm.
   */
  capturedCount: number
  /** True while the delete mutation is in flight. */
  submitting: boolean
  /** Error message to surface; null hides the alert row. */
  error: string | null
  /** Called when the user dismisses without confirming (Cancel/Esc/overlay). */
  onCancel: () => void
  /** Called when the user taps the destructive confirm button. */
  onConfirm: () => void
}

export function DeleteRoomDialog({
  open,
  roomName,
  capturedCount,
  submitting,
  error,
  onCancel,
  onConfirm,
}: DeleteRoomDialogProps): ReactElement {
  const isLoud = capturedCount > 0
  const title = isLoud ? `Delete ${roomName}?` : 'Delete this room?'
  const description = isLoud
    ? `This will permanently delete ${capturedCount} captured ${
        capturedCount === 1 ? 'asset' : 'assets'
      } in ${roomName}. This can't be undone.`
    : `This removes ${roomName} from this building.`

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      dismissOnOverlayClick={!submitting}
    >
      <div className="flex flex-col gap-3" data-testid="delete-room-dialog">
        {error !== null ? (
          <Alert variant="error" title="Could not delete room">
            {error}
          </Alert>
        ) : null}
        {isLoud ? (
          <p
            className="text-sm text-gray-700"
            data-testid="delete-room-loud-warning"
          >
            Those captured counts will be removed from this building and from
            future exports. If you only meant to pause, close this dialog and
            keep capturing instead.
          </p>
        ) : null}
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Keep this room"
          >
            Keep room
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={submitting}
            aria-label="Confirm delete room"
            data-testid="confirm-delete-room-btn"
          >
            {submitting ? 'Deleting…' : 'Delete room'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// Pure helper: count the captured assets in a room for the loud-confirm
// threshold. "Captured" = a positive recorded quantity — these are the
// contractual baseline counts a hard delete destroys. A zero-confirmed
// item ("checked, none here") is NOT counted: deleting it loses no
// recorded quantity, so it should not trigger the loud warning. Kept pure
// and exported so the threshold semantics are unit-testable and identical
// at every call site.
export function countCapturedAssets(
  items: ReadonlyArray<{ quantity: string }>,
): number {
  let count = 0
  for (const item of items) {
    if (parseFloat(item.quantity) > 0) count += 1
  }
  return count
}
